/**
 * Analytics-consent store (GDPR opt-in gate for product analytics).
 *
 * PostHog is initialized opted-OUT by default (see deps/posthog/client.ts), so
 * no events or pageviews are captured until the user explicitly opts in here.
 * This module is the single read/write API feature code uses for that decision;
 * it never imports posthog-js directly — it routes through
 * `applyAnalyticsConsent` so the live instance opts in/out immediately.
 *
 * Tri-state, mirroring the localStorage pattern of the sibling
 * marketing-consent module but simpler (no re-prompt cooldown):
 *   - 'granted' → user opted in, capture is active
 *   - 'denied'  → user opted out, capture stays a no-op
 *   - null      → undecided, show the banner
 */

import { applyAnalyticsConsent } from '../posthog/client';

const STORAGE_KEY = 'naumu:analytics-consent';

/**
 * Fired on `window` whenever the decision changes, so consumers that are not
 * PostHog (e.g. Google Tag Manager's Consent Mode) can react without polling.
 * `detail` is the new AnalyticsConsent value.
 */
export const ANALYTICS_CONSENT_EVENT = 'naumu:analytics-consent-change';

export type AnalyticsConsent = 'granted' | 'denied';

/**
 * Read the persisted analytics-consent decision.
 * Returns null when the user has not decided yet (banner should show).
 * SSR-safe and resilient to malformed/unavailable storage.
 */
export const getAnalyticsConsent = (): AnalyticsConsent | null => {
	if (typeof window === 'undefined') return null;
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		if (stored === 'granted') return 'granted';
		if (stored === 'denied') return 'denied';
		return null;
	} catch {
		return null;
	}
};

/**
 * Persist the user's analytics-consent decision AND apply it to the live
 * PostHog instance immediately (opt in / opt out of capture).
 */
export const setAnalyticsConsent = (granted: boolean): void => {
	if (typeof window !== 'undefined') {
		try {
			localStorage.setItem(STORAGE_KEY, granted ? 'granted' : 'denied');
		} catch {
			// Storage may be unavailable (private mode, quota). The live opt-in/out
			// below still takes effect for this session.
		}
	}
	applyAnalyticsConsent(granted);
	if (typeof window !== 'undefined') {
		window.dispatchEvent(
			new CustomEvent<AnalyticsConsent>(ANALYTICS_CONSENT_EVENT, {
				detail: granted ? 'granted' : 'denied',
			})
		);
	}
};

/**
 * Subscribe to consent changes. Returns an unsubscribe function. The callback
 * is NOT invoked with the current value — read it with getAnalyticsConsent().
 */
export const subscribeAnalyticsConsent = (
	listener: (consent: AnalyticsConsent) => void
): (() => void) => {
	if (typeof window === 'undefined') return () => {};
	const handler = (event: Event) => {
		listener((event as CustomEvent<AnalyticsConsent>).detail);
	};
	window.addEventListener(ANALYTICS_CONSENT_EVENT, handler);
	return () => window.removeEventListener(ANALYTICS_CONSENT_EVENT, handler);
};
